import { VerticalInventoryQuantityRow } from "./physical-control-group-entry";
import { PhysicalControlGroupLayout } from "./physical-inventory-details";
import { removeTrailing } from "./utils";

// raw entities from the server
export class RawPhysicalInventoryConflicts {
    public reportId: string = '';
    public reportName: string = '';
    public reportDate: Date | null = null;
    conflictsRemaining: number = 0;

    public conflicts: RawPhysicalInventoryConflictItem[] = [];
}

export class RawPhysicalInventoryConflictItem {
    public controlNumber: string = '';
    public controlName: string = '';
    public serviceCenter: string = '';
    public partNumber: string = '';
    public description: string = '';
    public lineNumber: string = '';
    public columnNumber: string = '';
    public type: string = '';
    public subType: string = '';
    public unitOfMeasure: string = '';
    public layout: PhysicalControlGroupLayout = PhysicalControlGroupLayout.horizontal;

    public entry1: RawPhysicalInventoryConflictEntry = new RawPhysicalInventoryConflictEntry();
    public entry2: RawPhysicalInventoryConflictEntry = new RawPhysicalInventoryConflictEntry();
}

export class RawPhysicalInventoryConflictEntry {
    public quantities: (number | null)[] = [];
}

// view models for the UI
export class PhysicalInventoryConflicts {


    constructor(raw?: RawPhysicalInventoryConflicts)
    {
        this.conflicts = [];

        if (raw){
            this.reportId = raw.reportId;
            this.reportName = raw.reportName;
            this.reportDate = raw.reportDate;
            this.conflictsRemaining = raw.conflictsRemaining;
            
            for (let conflict of raw.conflicts) {
                if (conflict.layout == PhysicalControlGroupLayout.horizontal) {
                    this.conflicts.push(new HorizontalPhysicalInventoryConflictItem(conflict));
                }
                else if (conflict.layout == PhysicalControlGroupLayout.vertical) {
                    this.conflicts.push(new VerticalPhysicalInventoryConflictItem(conflict));
                }
            }
        }
    }

	public reportId: string = '';
    public reportName: string = '';
	public reportDate: Date | null = null;
	conflictsRemaining: number = 0;

    public conflicts: PhysicalInventoryConflictItemBase[];
}

export class PhysicalInventoryConflictItemBase {

    constructor(raw: RawPhysicalInventoryConflictItem) {
        this.controlNumber = raw.controlNumber;
        this.controlName = raw.controlName;
        this.serviceCenter = raw.serviceCenter;
        this.partNumber = raw.partNumber;
        this.description = raw.description;
        this.lineNumber = raw.lineNumber;
        this.columnNumber = raw.columnNumber;
        this.type = raw.type;
        this.subType = raw.subType;
        this.unitOfMeasure = raw.unitOfMeasure;
        this.layout = raw.layout;
    }

    public controlNumber: string = '';
    public controlName: string = '';
    public serviceCenter: string = '';
    public partNumber: string = '';
    public description: string = '';
    public lineNumber: string = '';
    public columnNumber: string = '';
    public type: string = '';
    public subType: string = '';
    public unitOfMeasure: string = '';
    public layout: PhysicalControlGroupLayout = PhysicalControlGroupLayout.horizontal;
    public isResolved: boolean = false;
}

export class HorizontalPhysicalInventoryConflictItem extends PhysicalInventoryConflictItemBase {

    constructor(raw: RawPhysicalInventoryConflictItem) {
        super(raw);
        this.entry1 = new HorizontalPhysicalInventoryConflictEntry(raw.entry1);
        this.entry2 = new HorizontalPhysicalInventoryConflictEntry(raw.entry2);
    }

    public entry1: HorizontalPhysicalInventoryConflictEntry;
    public entry2: HorizontalPhysicalInventoryConflictEntry;
}

export class VerticalPhysicalInventoryConflictItem extends PhysicalInventoryConflictItemBase {
    constructor(raw: RawPhysicalInventoryConflictItem) {
        super(raw);
        this.entry1 = new VerticalPhysicalInventoryConflictEntry(raw.entry1);
        this.entry2 = new VerticalPhysicalInventoryConflictEntry(raw.entry2);
    }

    public entry1: VerticalPhysicalInventoryConflictEntry;
    public entry2: VerticalPhysicalInventoryConflictEntry;
}

export class HorizontalPhysicalInventoryConflictEntry {

    constructor(raw: RawPhysicalInventoryConflictEntry) {
        this.quantity1 = raw.quantities[0] || 0;
        this.quantity2 = raw.quantities[1] || 0;
        this.quantity3 = raw.quantities[2] || 0;
        this.quantity4 = raw.quantities[3] || 0;
        this.quantity5 = raw.quantities[4] || 0;
        this.quantity6 = raw.quantities[5] || 0;

        this.total = this.getTotal();
    }

    public quantity1: any;
    public quantity2: any;
    public quantity3: any;
    public quantity4: any;
    public quantity5: any;
    public quantity6: any;

    public getQuantities(): (number | null)[] {
        var result = [
            this.quantity1 || null,
            this.quantity2 || null,
            this.quantity3 || null,
            this.quantity4 || null,
            this.quantity5 || null,
            this.quantity6 || null
        ];

        removeTrailing(result);
        return result;
    }

    public total: number = 0;

    public getTotal(): number {
        return this.quantity1 + this.quantity2 + this.quantity3 + this.quantity4 + this.quantity5 + this.quantity6;
    }
}

export class VerticalPhysicalInventoryConflictEntry {

    constructor(raw: RawPhysicalInventoryConflictEntry) {
        let i = 0;

        for (i = 0; i < raw.quantities.length; i++) {
            if (i % 2 === 0) {
                this.quantities.push(new VerticalInventoryQuantityRow());
                this.quantities[i / 2].quantity1 = raw.quantities[i] || 0;
            }
            else {
                this.quantities[Math.floor(i / 2)].quantity2 = raw.quantities[i] || 0;
            }
        }

        // finish out the rows
        for (i = i; i < 12; i++) {
            if (i % 2 === 0) {
                this.quantities.push(new VerticalInventoryQuantityRow());
                this.quantities[i / 2].quantity1 = raw.quantities[i] || 0;
            }
            else {
                this.quantities[Math.floor(i / 2)].quantity2 = raw.quantities[i] || 0;
            }
        }
        this.total = this.getTotal();
    }

    public quantities: VerticalInventoryQuantityRow[] = [];
    public total: number = 0;

    public getQuantities(): (number | null)[] {

        var result = [] as (number | null)[];
        for (let quantity of this.quantities) {
            result.push(quantity.quantity1);
            result.push(quantity.quantity2);
        }

        removeTrailing(result);

        return result;
    }

    public getTotal(): number {
        let total = 0;
        for (let row of this.quantities) {
            total += row.quantity1 + row.quantity2;
        }
        return total;
    }
}